import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Users } from "lucide-react";
import { Spinner } from "../../components/ui/spinner";
import { ReactSelect } from "../../components/ui/react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Country, State } from "country-state-city";
import { AddressAutocomplete } from "../../components/ui/address-autocomplete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { eventBus } from "../../core/utils/eventBus";

const CustomerModal = ({
  open,
  onOpenChange,
  initialData = null,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const [modalData, setModalData] = useState({
    id: null,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    street_address: "",
    city: "",
    postcode: "",
    house_number: "",
    state: "",
    country: "",
    company: "",
    customer_group_id: "",
    customer_type: "individual",
    heard_about_us: "",
    tax_class: "",
    company_reg_no: "",
    contact_person: "",
    contact_person_phone: "",
    contact_person_email: "",
    contact_person_relation: "",
    email_notifications: false,
    status: "active",
    registration_store_id: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setModalData({ ...initialData });
      } else {
        setModalData({
          id: null,
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          street_address: "",
          city: "",
          postcode: "",
          house_number: "",
          state: "",
          country: "",
          company: "",
          customer_group_id: "",
          customer_type: "individual",
          heard_about_us: "",
          tax_class: "",
          company_reg_no: "",
          contact_person: "",
          contact_person_phone: "",
          contact_person_email: "",
          contact_person_relation: "",
          email_notifications: false,
          status: "active",
          registration_store_id: "",
        });
      }
      setErrors({});
    }
  }, [open, initialData]);

  const { data: groudData = [] } = useQuery({
    queryKey: ["customer-groups"],
    queryFn: async () => {
      const res = await apiClient.get("/customer_group");
      return res.data.data || [];
    },
    enabled: open,
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get("/store");
      return res.data.data || [];
    },
    enabled: open,
  });

  const saveCustomerMutation = useMutation({
    mutationFn: async (data) => {
      const url = data.id
        ? `/customer/${data.id}`
        : "/customer";
      const method = data.id ? "put" : "post";
      return await apiClient[method](url, data);
    },
    onSuccess: (res, variables) => {
      if (!variables.id) {
        eventBus.publish("customer:created", {
          ...res.data.data,
          email_notifications: variables.email_notifications,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success(
        `Customer ${variables.id ? "updated" : "added"} successfully!`,
      );
      if (onSuccess) onSuccess(res.data.data);
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Something went wrong!");
    },
  });

  const customerTypes = [
    { value: "individual", label: "Individual" },
    { value: "company", label: "Company" },
    { value: "organization", label: "Organization" },
    { value: "government", label: "Government" },
    { value: "non_profit", label: "Non-Profit" },
    { value: "other", label: "Other" },
  ];

  const countries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [],
  );

  const selectedCountryCode = useMemo(() => {
    const country = Country.getAllCountries().find(
      (c) => c.name === modalData.country,
    );
    return country ? country.isoCode : "";
  }, [modalData.country]);

  const states = useMemo(
    () =>
      selectedCountryCode
        ? State.getStatesOfCountry(selectedCountryCode).map((s) => ({
          value: s.isoCode,
          label: s.name,
        }))
        : [],
    [selectedCountryCode],
  );

  const validateForm = () => {
    const newErrors = {};

    if (!modalData.customer_group_id) {
      newErrors.customer_group_id = "Customer group is required";
    }

    if (!modalData.first_name?.trim()) {
      newErrors.first_name = "First name is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (modalData.email?.trim() && !emailRegex.test(modalData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    saveCustomerMutation.mutate(modalData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto px-1">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} />
            {modalData.id ? "Edit Customer" : "Add Customer"}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-muted/50">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger
                value="company"
                disabled={modalData.customer_type === "individual"}
              >
                Company
              </TabsTrigger>
              <TabsTrigger value="address">Address</TabsTrigger>
              <TabsTrigger value="additional">Additional</TabsTrigger>
            </TabsList>

            <div className="py-4">
              <TabsContent value="general" className="space-y-4 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      Customer Group <span className="text-destructive">*</span>
                    </Label>
                    <ReactSelect
                      options={groudData.map((group) => ({
                        value: String(group.id),
                        label: group.name,
                      }))}
                      value={
                        groudData
                          .map((g) => ({
                            value: String(g.id),
                            label: g.name,
                          }))
                          .find(
                            (opt) =>
                              opt.value === String(modalData.customer_group_id),
                          ) || null
                      }
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          customer_group_id: opt ? opt.value : "",
                        })
                      }
                      placeholder="Select Group"
                    />
                    {errors.customer_group_id && (
                      <p className="text-destructive text-sm">
                        {errors.customer_group_id}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Customer Type <span className="text-destructive">*</span>
                    </Label>
                    <ReactSelect
                      options={customerTypes}
                      value={
                        customerTypes.find(
                          (ct) => ct.value === modalData.customer_type,
                        ) || null
                      }
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          customer_type: opt ? opt.value : "individual",
                        })
                      }
                      placeholder="Select Type"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      First Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      placeholder="Enter first name"
                      value={modalData.first_name}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          first_name: e.target.value,
                        })
                      }
                      className={errors.first_name ? "border-destructive" : ""}
                    />
                    {errors.first_name && (
                      <p className="text-destructive text-sm">
                        {errors.first_name}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      placeholder="Enter last name"
                      value={modalData.last_name}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          last_name: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Email address</Label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={modalData.email}
                      onChange={(e) =>
                        setModalData({ ...modalData, email: e.target.value })
                      }
                      className={errors.email ? "border-destructive" : ""}
                    />
                    {errors.email && (
                      <p className="text-destructive text-sm">{errors.email}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <PhoneInput
                      country={"au"}
                      value={modalData.phone}
                      onChange={(phone) =>
                        setModalData({ ...modalData, phone })
                      }
                      inputClass={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 ${errors.phone ? "border-destructive" : ""}`}
                      containerClass="w-100"
                    />
                    {errors.phone && (
                      <p className="text-destructive text-sm">{errors.phone}</p>
                    )}
                  </div>

                  <div className="space-y-2 lg:col-span-2">
                    <Label>Registration Store</Label>
                    <ReactSelect
                      options={stores.map((store) => ({
                        value: store.id,
                        label: store.name,
                      }))}
                      value={
                        stores
                          .map((s) => ({ value: s.id, label: s.name }))
                          .find(
                            (o) => o.value === modalData.registration_store_id,
                          ) || null
                      }
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          registration_store_id: opt ? opt.value : "",
                        })
                      }
                      placeholder="Select Store"
                      isClearable
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="company" className="space-y-4 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      placeholder="Enter company name"
                      value={modalData.company}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          company: e.target.value,
                        })
                      }
                      className={errors.company ? "border-destructive" : ""}
                    />
                    {errors.company && (
                      <p className="text-destructive text-sm">
                        {errors.company}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Company Registration Number</Label>
                    <Input
                      placeholder="Enter registration number"
                      value={modalData.company_reg_no}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          company_reg_no: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person</Label>
                    <Input
                      placeholder="Enter contact person name"
                      value={modalData.contact_person}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          contact_person: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person Phone</Label>
                    <PhoneInput
                      country={"au"}
                      value={modalData.contact_person_phone}
                      onChange={(phone) =>
                        setModalData({
                          ...modalData,
                          contact_person_phone: phone,
                        })
                      }
                      inputClass="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                      containerClass="w-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person Email</Label>
                    <Input
                      type="email"
                      placeholder="Enter contact email"
                      value={modalData.contact_person_email}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          contact_person_email: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person Relation</Label>
                    <Input
                      placeholder="Enter relation"
                      value={modalData.contact_person_relation}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          contact_person_relation: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="address" className="space-y-4 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>
                      {modalData.customer_type !== "individual"
                        ? "Unit Number"
                        : "House Number"}
                    </Label>
                    <Input
                      placeholder={
                        modalData.customer_type !== "individual"
                          ? "Enter unit number"
                          : "Enter house number"
                      }
                      value={modalData.house_number}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          house_number: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Street Address</Label>
                    <AddressAutocomplete
                      placeholder="Search for address…"
                      value={modalData.street_address}
                      onChange={(val) =>
                        setModalData({ ...modalData, street_address: val })
                      }
                      onPlaceSelect={({
                        address,
                        city,
                        state,
                        country,
                        postcode,
                      }) =>
                        setModalData((prev) => ({
                          ...prev,
                          street_address: address || prev.street_address,
                          city: city || prev.city,
                          state: state || prev.state,
                          country: country || prev.country,
                          postcode: postcode || prev.postcode,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <ReactSelect
                      options={countries}
                      value={countries.find(
                        (c) => c.label === modalData.country,
                      )}
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          country: opt ? opt.label : "",
                          state: "",
                          city: "",
                        })
                      }
                      placeholder="Select Country"
                      isClearable
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <ReactSelect
                      options={states}
                      value={states.find((s) => s.label === modalData.state)}
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          state: opt ? opt.label : "",
                          city: "",
                        })
                      }
                      placeholder="Select State"
                      isClearable
                      isDisabled={!modalData.country}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      placeholder="Enter city"
                      value={modalData.city}
                      onChange={(e) =>
                        setModalData({ ...modalData, city: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      placeholder="Enter postcode"
                      value={modalData.postcode}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          postcode: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="additional" className="space-y-4 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <ReactSelect
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ]}
                      value={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" },
                      ].find((o) => o.value === modalData.status)}
                      onChange={(opt) =>
                        setModalData({
                          ...modalData,
                          status: opt ? opt.value : "active",
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>How did you hear about us?</Label>
                    <Input
                      placeholder="Enter source"
                      value={modalData.heard_about_us}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          heard_about_us: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Class</Label>
                    <Input
                      placeholder="Enter tax class"
                      value={modalData.tax_class}
                      onChange={(e) =>
                        setModalData({
                          ...modalData,
                          tax_class: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="emailNotifications"
                      checked={modalData.email_notifications}
                      onCheckedChange={(checked) =>
                        setModalData({
                          ...modalData,
                          email_notifications: checked,
                        })
                      }
                    />
                    <Label
                      htmlFor="emailNotifications"
                      className="cursor-pointer"
                    >
                      Enable Email Notifications
                    </Label>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        <DialogFooter className="justify-start border-t p-5">
          <Button
            onClick={handleSave}
            disabled={saveCustomerMutation.isPending}
          >
            {saveCustomerMutation.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerModal;
